import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext.js';
import { useAuthStore } from '../state/authStore.js';
import { useImpersonationStore } from '../state/impersonationStore.js';
import { ImpersonationView } from '../pages/impersonation/ImpersonationView.js';
import { RouteErrorBoundary } from './ErrorBoundary.js';
import { watchForNewDeploy } from '../lib/deployWatcher.js';

const NAV_ITEMS: Array<{ to: string; labelKey: Parameters<ReturnType<typeof useI18n>['t']>[0] }> = [
  { to: '/products', labelKey: 'nav.products' },
  { to: '/categories', labelKey: 'nav.categories' },
  { to: '/discount-reasons', labelKey: 'nav.discountReasons' },
  { to: '/staff', labelKey: 'nav.staff' },
  { to: '/terminals', labelKey: 'nav.terminals' },
  { to: '/payment-terminals', labelKey: 'nav.paymentTerminals' },
  { to: '/reports', labelKey: 'nav.reports' },
  { to: '/shifts', labelKey: 'nav.shifts' },
  { to: '/sync', labelKey: 'nav.sync' },
  { to: '/ticket', labelKey: 'nav.ticket' },
];

export function Layout() {
  // A tab left open across deploys keeps running the old bundle forever —
  // the 2026-08-02 stale-timezone-fix incident. When the served bundle
  // changes, offer one visible, non-blocking reload.
  const [newDeploy, setNewDeploy] = useState(false);
  useEffect(() => watchForNewDeploy(() => setNewDeploy(true)), []);
  const { t } = useI18n();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const target = useImpersonationStore((s) => s.target);
  const stopImpersonation = useImpersonationStore((s) => s.stop);

  if (target) {
    return (
      <div className="app-shell app-shell--impersonating">
        <div className="impersonation-band">
          <span>{t('impersonation.bannerLabel', { name: target.displayName })}</span>
          <button type="button" onClick={stopImpersonation}>
            {t('impersonation.exit')}
          </button>
        </div>
        <div className="main" style={{ gridColumn: '1 / -1' }}>
          <div className="content">
            <RouteErrorBoundary>
              <ImpersonationView target={target} />
            </RouteErrorBoundary>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      {newDeploy && (
        <div className="deploy-banner" role="status">
          <span>{t('app.newVersion')}</span>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => window.location.reload()}>
            {t('app.reloadNow')}
          </button>
        </div>
      )}
      <nav className="sidebar" aria-label={t('app.title')}>
        <div className="sidebar__brand">{t('app.title')}</div>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `sidebar__link${isActive ? ' sidebar__link--active' : ''}`}
          >
            {t(item.labelKey)}
          </NavLink>
        ))}
        <div className="sidebar__footer">
          {user && <div className="field-hint">{user.email}</div>}
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => logout()}>
            {t('common.signOut')}
          </button>
        </div>
      </nav>
      <div className="main">
        <div className="content">
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </div>
      </div>
    </div>
  );
}
